import {
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
} from 'firebase/firestore'
import {
  ref,
  getDownloadURL,
} from 'firebase/storage'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import store from '@/store'
import i18n from '@/libs/i18n'
import axios from '@axios'

export default {
  namespaced: true,
  state: {
    eligiblePages: [],
    selectedPage: null,
    previewVisible: true, // Show preview by default for better UX
    isLoading: false,
    currentFormSchema: null,
    currentViewport: 'mobile',
  },
  getters: {
    getEligiblePages: state => state.eligiblePages,
    getSelectedPage: state => state.selectedPage,
    isPreviewVisible: state => state.previewVisible,
    isLoading: state => state.isLoading,
    getCurrentFormSchema: state => state.currentFormSchema,
    getCurrentViewport: state => state.currentViewport,
  },
  mutations: {
    SET_ELIGIBLE_PAGES(state, pages) {
      state.eligiblePages = pages
    },
    SET_SELECTED_PAGE(state, page) {
      state.selectedPage = page
    },
    SET_PREVIEW_VISIBILITY(state, visible) {
      state.previewVisible = visible
    },
    SET_LOADING(state, loading) {
      state.isLoading = loading
    },
    UPDATE_FORM_SCHEMA(state, schema) {
      state.currentFormSchema = schema
    },
    SET_VIEWPORT(state, viewport) {
      state.currentViewport = viewport
    },
    CLEAR_PREVIEW_STATE(state) {
      state.selectedPage = null
      state.previewVisible = false
      state.currentFormSchema = null
      state.currentViewport = 'mobile'
    },
  },
  actions: {
    /**
     * Fetch pages that are eligible for form preview
     * Only pages with formId not null are eligible
     */
    async fetchEligiblePages({ commit }) {
      commit('SET_LOADING', true)
      
      try {
        const { pages } = tenantCtx()
        
        const q = query(
          pages,
          where('formId', '!=', null),
          where('deletedAt', '==', null),
          orderBy('formId'), // Required for != queries
          orderBy('updatedAt', 'desc')
        )
        
        const querySnapshot = await getDocs(q)
        const eligiblePages = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        
        // Filter out pages where formId is undefined or 'undefined' string
        const filteredPages = eligiblePages.filter(page => 
          page.formId && 
          page.formId !== 'undefined' && 
          typeof page.formId === 'string'
        )
        
        commit('SET_ELIGIBLE_PAGES', filteredPages)
        return filteredPages
      } catch (error) {
        console.error('Error fetching eligible pages:', error)
        
        store.dispatch('notify', {
          body: i18n.t('Unable to load pages for preview'),
          variant: 'warning',
        })
        
        commit('SET_ELIGIBLE_PAGES', [])
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Select a page for preview and fetch its content from Google Cloud Storage
     */
    async selectPageForPreview({ commit, dispatch }, pageId) {
      if (!pageId) {
        commit('SET_SELECTED_PAGE', null)
        return
      }

      commit('SET_LOADING', true)
      
      try {
        // Fetch page data from Firestore
        const pageSnapshot = await dispatch('pages/fetchById', pageId, { root: true })
        
        if (pageSnapshot && pageSnapshot.exists()) {
          const pageData = {
            id: pageSnapshot.id,
            ...pageSnapshot.data()
          }
          
          // Fetch HTML and CSS from Google Cloud Storage
          const { pagesStorage } = tenantCtx()
          
          let pageHtml = ''
          let pageCSS = ''
          
          try {
            // Fetch HTML from storage
            const htmlRef = ref(pagesStorage, `${pageId}/index.html`)
            const htmlUrl = await getDownloadURL(htmlRef)
            const htmlResponse = await axios.get(htmlUrl)
            pageHtml = htmlResponse.data
          } catch (htmlError) {
            console.warn('Could not fetch page HTML from storage:', htmlError)
            // If HTML is not found in storage, we'll use an empty template
            pageHtml = `
              <!DOCTYPE html>
              <html>
              <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1">
                <title>Preview</title>
              </head>
              <body>
                <div id="formio"></div>
              </body>
              </html>
            `
          }
          
          try {
            // Fetch CSS from storage
            const cssRef = ref(pagesStorage, `${pageId}/styles.css`)
            const cssUrl = await getDownloadURL(cssRef)
            const cssResponse = await axios.get(cssUrl)
            pageCSS = cssResponse.data
          } catch (cssError) {
            console.warn('Could not fetch page CSS from storage:', cssError)
            // CSS is optional, so we'll just use empty string if not found
            pageCSS = ''
          }
          
          // Add the HTML and CSS to the page data
          const pageWithAssets = {
            ...pageData,
            html: pageHtml,
            css: pageCSS
          }
          
          commit('SET_SELECTED_PAGE', pageWithAssets)
          commit('SET_PREVIEW_VISIBILITY', true)
          
          return pageWithAssets
        } else {
          throw new Error('Page not found')
        }
      } catch (error) {
        console.error('Error selecting page for preview:', error)
        
        store.dispatch('notify', {
          body: i18n.t('Unable to load selected page'),
          variant: 'danger',
        })
        
        commit('SET_SELECTED_PAGE', null)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Update the form schema for real-time preview
     */
    updateFormPreview({ state, commit, dispatch }, schema) {
      commit('UPDATE_FORM_SCHEMA', schema)
      
      // If preview is visible and a page is selected, trigger re-render
      if (state.previewVisible && state.selectedPage && schema) {
        // The re-render will be handled by watchers in the components
        console.debug('Form schema updated for preview')
      }
    },

    /**
     * Toggle preview visibility
     */
    togglePreviewVisibility({ commit, state }) {
      const newVisibility = !state.previewVisible
      commit('SET_PREVIEW_VISIBILITY', newVisibility)
      
      // If hiding preview, we might want to clear some state
      if (!newVisibility) {
        // Keep selected page but hide preview
      }
      
      return newVisibility
    },

    /**
     * Clear all preview state
     */
    clearPreviewState({ commit }) {
      commit('CLEAR_PREVIEW_STATE')
    },

    /**
     * Initialize preview state for a specific form
     */
    async initializePreview({ dispatch, commit }, formId) {
      try {
        // Fetch eligible pages
        await dispatch('fetchEligiblePages')
        
        // Fetch active theme
        await dispatch('themes/fetchActiveTheme', null, { root: true })
        
        // Clear selected page and form schema, but keep preview visible
        commit('SET_SELECTED_PAGE', null)
        commit('UPDATE_FORM_SCHEMA', null)
        commit('SET_VIEWPORT', 'mobile')
        
        console.debug('Preview initialized for form:', formId)
      } catch (error) {
        console.error('Error initializing preview:', error)
      }
    }
  }
}